import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { useCollection, useCurrentWorkspace } from "~/api/queries";
import { lazy, Suspense } from "react";
const AnalysisInputView = lazy(async () => ({
  default: (await import("~/features/analysis/AnalysisScene")).AnalysisInputView,
}));
import { useWorkspaceGate, WorkspaceGateScreen } from "~/features/work/workspaceGate";
import { Button } from "~/foundation/components/Button";
import { Spinner } from "~/foundation/components/Spinner";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { collectionInputKey, useCollectionInput } from "./collectionInput";
import { newsDestination } from "./newsDestinations";

export function WorkspaceNewsCollectionScene(props: {
  collectionId: string;
  view: string;
  ws?: string;
}) {
  const { gate, hold } = useWorkspaceGate(props.ws);
  return hold ? <WorkspaceGateScreen gate={gate} /> : <NewsCollectionScene {...props} />;
}

/** News supplies articles. Knowledge has one shared gallery; no second application lives in a collection. */
export function NewsCollectionScene({
  collectionId,
  view,
  ws,
}: {
  collectionId: string;
  view: string;
  ws?: string;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const client = useQueryClient();
  const collection = useCollection(collectionId);
  const workspace = useCurrentWorkspace();
  const input = useCollectionInput(collectionId);
  useDocumentTitle(collection.data?.name ?? t("News input"));
  const refresh = useMutation({
    mutationFn: () => api.research.post("channels.collections.refresh", { collectionId }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: collectionInputKey(collectionId) }),
        client.invalidateQueries({ queryKey: ["analysis"] }),
      ]);
    },
  });
  const enabled = useMutation({
    mutationFn: () =>
      api.research.post("channels.collections.enabled", {
        collectionId,
        enabled: !input.data?.enabled,
      }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: collectionInputKey(collectionId) }),
        client.invalidateQueries({ queryKey: ["analysis"] }),
      ]);
    },
  });
  if (input.isLoading || collection.isLoading) return <Spinner label={t("Loading")} />;
  if (input.isError || !input.data || !collection.data)
    return <p className="p-6">{t("This collection has no available news input.")}</p>;
  const source = input.data;
  const workspaceId = ws ?? workspace.data?.id;
  const destination = newsDestination(view, collectionId, workspaceId);
  if (source.canExplore && destination)
    return <Navigate to={destination.to as never} search={destination.search as never} replace />;
  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-6">
      <header>
        <h1 className="text-h2 font-semibold">
          {collection.data.name} · {t("News input")}
        </h1>
        <p>
          {t(
            "News is an input. Its subjects, connections and stories live alongside the rest of your knowledge.",
          )}
        </p>
      </header>
      <div className="flex flex-wrap gap-4">
        <Link
          to={"/knowledge/visuals" as never}
          search={{ audience: "workspace", ws: workspaceId, collection: collectionId } as never}
          className="text-accent underline"
        >
          {t("Open in Knowledge gallery")}
        </Link>
        <Link
          to="/collections/$collectionId"
          params={{ collectionId }}
          search={{ tab: "documents" }}
          className="text-accent underline"
        >
          {t("Collection documents")}
        </Link>
      </div>
      <details className="rounded-md border border-divider p-4">
        <summary>
          {t("Input status and controls")} ·{" "}
          {source.enabled ? t("Input enabled") : t("Input paused")}
        </summary>
        <p>
          {t("{{count}} articles available · {{deposited}} kept in this collection", {
            count: source.total,
            deposited: source.deposited,
          })}
        </p>
        <p>
          {t("Last deposit sync")}:{" "}
          {source.lastSyncedAt ? new Date(source.lastSyncedAt).toLocaleString() : "—"}
        </p>
        {source.canManage && (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              disabled={refresh.isPending || !source.enabled}
              onClick={() => refresh.mutate()}
            >
              {t("Refresh collection")}
            </Button>
            <Button variant="ghost" disabled={enabled.isPending} onClick={() => enabled.mutate()}>
              {source.enabled ? t("Pause input") : t("Resume input")}
            </Button>
          </div>
        )}
      </details>
      {(source.error || refresh.isError || enabled.isError) && (
        <p role="alert">
          {source.error ?? t("The news connection could not complete that request. Try again.")}
        </p>
      )}
      {!source.enabled && (
        <p role="status">
          {t("New deposits are paused. Existing news and documents remain available.")}
        </p>
      )}
      {source.canExplore && workspaceId ? (
        <Suspense fallback={<Spinner label={t("Loading")} />}>
          <AnalysisInputView
            scope={{ workspaceId, source: "news", collectionId, days: 45 }}
            view="overview"
          />
        </Suspense>
      ) : (
        <p>
          {t(
            "Read the imported articles in Collection documents. The full source analysis requires membership of its source workspace.",
          )}
        </p>
      )}
    </section>
  );
}
